// The signed-in member's own account (spec/13 §2's staff menu; spec/07 §3.3, §6.6, §6.7; spec/16 §3), as
// TanStack Query hooks over the generated client:
//
//   GET    /v1/me/sessions                                   where you are signed in      (F-IAM-12)
//   DELETE /v1/me/sessions/{sessionId}                       end one of them              (F-IAM-12)
//   GET    /v1/organizations/{o}/members/me/pins             where you may set it, and where it is set (F-IAM-10)
//   PUT    /v1/organizations/{o}/members/me/pin              set your staff PIN at a venue (F-IAM-10)
//   POST   /v1/auth/two-factor/generate-backup-codes         new backup codes             (F-IAM-09)
//   POST   /v1/auth/two-factor/disable                       turn the second factor off   (F-IAM-09)
//   GET    /v1/organizations/{o}/notification-preferences    your preferences here        (F-NTF-01)
//   PUT    /v1/organizations/{o}/notification-preferences    change them                  (F-NTF-01)
//   GET    /v1/auth/list-accounts                            your password and linked providers (F-IAM-03 … F-IAM-05)
//   POST   /v1/auth/unlink-account                           unlink one of them           (ConnectedAccounts.tsx)
//
// None carries a permission: each is about the caller only. The notification preferences are the
// F-NTF-01 pair, organisation-scoped; the older `/v1/notifications/prefs` is the player's marketing
// consent and is not a staff setting.

import type { LinkedAccount, MyStaffPin, NotificationPreferences, PutNotificationPreferences, SignedInSession } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api/client';
import { queryKeys } from '../api/queries';

export const accountKeys = {
  sessions: ['me', 'sessions'] as const,
  linkedAccounts: ['me', 'linked-accounts'] as const,
  staffPins: (organizationId: string) => ['me', 'staff-pins', organizationId] as const,
  notificationPreferences: (organizationId: string) => ['me', 'notification-preferences', organizationId] as const,
};

/** Your signed-in sessions that have not expired, newest first. */
export function useMySessions() {
  const api = useApi();
  return useQuery<readonly SignedInSession[]>({
    queryKey: accountKeys.sessions,
    queryFn: async () => (await api.listMySessions()).data,
  });
}

/** Your ways in: the password and every provider linked to your account. */
export function useLinkedAccounts() {
  const api = useApi();
  return useQuery<readonly LinkedAccount[]>({
    queryKey: accountKeys.linkedAccounts,
    queryFn: async () => {
      const body: unknown = await api.listUserAccounts();
      return Array.isArray(body) ? (body as LinkedAccount[]) : [];
    },
    retry: false,
  });
}

/** End one of your sessions; it stops working on its next request. */
export function useRevokeMySession() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api.revokeMySession({ path: { sessionId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: accountKeys.sessions }),
  });
}

/**
 * Every venue of the organisation you work at, with whether your PIN is set and locked there (spec/07
 * §6.7) — the server's answer, so an owner or an every-venue member is offered every live venue and a
 * venue-scoped member theirs: exactly where `setMyStaffPin` accepts a PIN.
 */
export function useMyStaffPins(organizationId: string, enabled = true) {
  const api = useApi();
  return useQuery<readonly MyStaffPin[]>({
    queryKey: accountKeys.staffPins(organizationId),
    queryFn: async () => (await api.listMyStaffPins({ path: { organizationId } })).data,
    enabled,
  });
}

/** Set or change your own six-digit PIN at one venue (spec/07 §6.7); the list above is read again. */
export function useSetMyStaffPin(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ venueId, pin }: { readonly venueId: string; readonly pin: string }) =>
      api.setMyStaffPin({ path: { organizationId }, headerParams: { 'x-clavian-venue': venueId }, body: { pin } }),
    // Settled, not only succeeded: a refusal for a locked PIN is news about the list too.
    onSettled: () => void queryClient.invalidateQueries({ queryKey: accountKeys.staffPins(organizationId) }),
  });
}

/** Replace the backup codes; the old ones stop working. Needs a session that proved the second factor. */
export function useGenerateBackupCodes() {
  const api = useApi();
  return useMutation({
    mutationFn: (password: string | undefined) => api.generateTwoFactorBackupCodes({ body: password === undefined ? {} : { password } }),
  });
}

/** Turn the second factor off. The session is re-read so the page shows it off. */
export function useDisableTwoFactor() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (password: string | undefined) => api.disableTwoFactor({ body: password === undefined ? {} : { password } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.session }),
  });
}

/** Your notification preferences at this organisation (spec/16 §3). */
export function useNotificationPreferences(organizationId: string) {
  const api = useApi();
  return useQuery<NotificationPreferences>({
    queryKey: accountKeys.notificationPreferences(organizationId),
    queryFn: () => api.getNotificationPreferences({ path: { organizationId } }),
  });
}

/** Change some of them; the answer is what now stands. */
export function usePutNotificationPreferences(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PutNotificationPreferences) => api.putNotificationPreferences({ path: { organizationId }, body }),
    onSuccess: (saved) => queryClient.setQueryData(accountKeys.notificationPreferences(organizationId), saved),
  });
}
